import { Plus, Trash2, GripVertical, Users } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const uid = () => Math.random().toString(36).slice(2, 12);

// Controlled structured services editor. `value` is an array of
// { id, name, price, duration, description, category, staff_ids, enabled }.
// Optional `staff` (array of {id,name}) enables per-service staff assignment.
export function ServicesEditor({ value = [], onChange, staff = [] }) {
  const list = value;
  const update = (i, patch) => onChange(list.map((s, x) => (x === i ? { ...s, ...patch } : s)));
  const add = () => onChange([...list, { id: uid(), name: "", price: "", duration: null, description: "", staff_ids: [], enabled: true }]);
  const remove = (i) => onChange(list.filter((_, x) => x !== i));
  const toggleStaff = (i, sid) => {
    const cur = list[i].staff_ids || [];
    update(i, { staff_ids: cur.includes(sid) ? cur.filter((x) => x !== sid) : [...cur, sid] });
  };

  return (
    <div className="space-y-3" data-testid="services-editor">
      {list.length === 0 && (
        <div className="rounded-lg border border-dashed border-slate-200 p-6 text-center text-sm text-slate-400">
          No services yet. Add the services you offer so your AI can quote them accurately.
        </div>
      )}
      {list.map((s, i) => (
        <div key={s.id || i} data-testid={`service-row-${i}`} className={`rounded-xl border p-4 ${s.enabled ? "border-slate-200 bg-white" : "border-slate-200 bg-slate-50 opacity-70"}`}>
          <div className="flex items-start gap-3">
            <GripVertical className="h-4 w-4 text-slate-300 mt-2.5 shrink-0" />
            <div className="flex-1 grid sm:grid-cols-12 gap-2">
              <div className="sm:col-span-5">
                <Label className="text-[11px] text-slate-400">Service name</Label>
                <Input data-testid={`service-name-${i}`} value={s.name} onChange={(e) => update(i, { name: e.target.value })} placeholder="e.g. Men's Haircut" className="mt-1 h-9" />
              </div>
              <div className="sm:col-span-3">
                <Label className="text-[11px] text-slate-400">Price (optional)</Label>
                <Input data-testid={`service-price-${i}`} value={s.price} onChange={(e) => update(i, { price: e.target.value })} placeholder="£25" className="mt-1 h-9" />
              </div>
              <div className="sm:col-span-4">
                <Label className="text-[11px] text-slate-400">Duration mins (optional)</Label>
                <Input data-testid={`service-duration-${i}`} type="number" value={s.duration ?? ""} onChange={(e) => update(i, { duration: e.target.value ? Number(e.target.value) : "" })} placeholder="30" className="mt-1 h-9" />
              </div>
              <div className="sm:col-span-6">
                <Label className="text-[11px] text-slate-400">Payment for this service</Label>
                <Select value={s.payment_policy || "inherit"} onValueChange={(v) => update(i, { payment_policy: v })}>
                  <SelectTrigger data-testid={`service-payment-${i}`} className="mt-1 h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inherit">Use business default</SelectItem>
                    <SelectItem value="none">No payment required</SelectItem>
                    <SelectItem value="deposit">Deposit required</SelectItem>
                    <SelectItem value="full">Full payment required</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(s.payment_policy === "deposit" || s.payment_policy === "full") && (
                <div className="sm:col-span-6">
                  <Label className="text-[11px] text-slate-400">Fixed charge (optional, overrides %)</Label>
                  <Input data-testid={`service-deposit-${i}`} type="number" value={s.deposit_amount ?? ""} onChange={(e) => update(i, { deposit_amount: e.target.value ? Number(e.target.value) : "" })} placeholder="e.g. 20" className="mt-1 h-9" />
                </div>
              )}
              <div className="sm:col-span-12">
                <Label className="text-[11px] text-slate-400">Short description (optional)</Label>
                <Textarea data-testid={`service-desc-${i}`} value={s.description} onChange={(e) => update(i, { description: e.target.value })} placeholder="What's included…" rows={2} className="mt-1" />
              </div>
              {staff.length > 0 && (
                <div className="sm:col-span-12">
                  <Label className="text-[11px] text-slate-400 flex items-center gap-1"><Users className="h-3 w-3" /> Who provides this? {(!s.staff_ids || s.staff_ids.length === 0) && <span className="text-slate-400">(any staff)</span>}</Label>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {staff.map((m) => {
                      const on = (s.staff_ids || []).includes(m.id);
                      return (
                        <button key={m.id} type="button" data-testid={`service-${i}-staff-${m.id}`} onClick={() => toggleStaff(i, m.id)}
                          className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors ${on ? "bg-blue-600 border-blue-600 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-blue-400"}`}>
                          {on ? "✓ " : ""}{m.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center justify-between mt-3 pl-7">
            <label className="flex items-center gap-2 text-xs text-slate-500">
              <Switch data-testid={`service-enabled-${i}`} checked={s.enabled} onCheckedChange={(v) => update(i, { enabled: v })} />
              {s.enabled ? "Active — offered by AI" : "Disabled — hidden from AI"}
            </label>
            <Button data-testid={`service-delete-${i}`} variant="ghost" size="sm" onClick={() => remove(i)} className="text-slate-400 hover:text-red-500 gap-1 h-8"><Trash2 className="h-3.5 w-3.5" /> Delete</Button>
          </div>
        </div>
      ))}
      <Button data-testid="add-service-btn" variant="outline" onClick={add} className="w-full gap-2 border-dashed"><Plus className="h-4 w-4" /> Add a service</Button>
    </div>
  );
}
